import { useMemo, useState } from "react";
import { useMyAppointmentCancelledAlerts } from "../../../../hooks/useMyAppointmentCancelledAlerts";
import { useResolveWorklistAlert } from "../../../../hooks/useResolveWorklistAlert";
import UiModalAlert from "../../../../components/modal-alert/ModalAlert.component";
import { Task } from "../../../../models/entities/task.model";
import { getSessionStorageUser } from "../../../../services/session/auth.service";
import WorklistSection from "../WorklistSection";
import { useWorklist } from "../WorklistContext";
import { sortAppointmentCancelledAlerts } from "../worklist.service";
import CriticalAlertRow from "../critical-alerts/CriticalAlertRow";

export const APPOINTMENT_CANCELLED_SECTION_ID = "appointment-cancelled";

/**
 * "Appointment cancelled" — a worklist section (KAN-2671, Maria 2026-10-29).
 *
 * Shows the "Appointment cancelled" task rule alerts (KAN-2667) assigned to the
 * logged-in user, ordered oldest first, with a blue edge like consent and the
 * same Call/Edit/Resolve actions. Section opens by default.
 *
 * v1 rule carries no appointment metadata so the row shows the details text +
 * alert date. Prod has no such rule yet so the section is empty in prod until
 * the rule is created.
 *
 * Plan: karchitecture/planning/kan-2671-appointment-cancelled-home-section.md.
 */
const AppointmentCancelledSection = () => {
	const sessionUser = getSessionStorageUser();
	const { alerts, count, isLoading, isError, invalidate, hideAlert } =
		useMyAppointmentCancelledAlerts(sessionUser?.id);
	// See useResolveWorklistAlert for resolve logic and optimistic-hide details.
	const { resolve, isPending } = useResolveWorklistAlert(hideAlert, sessionUser?.id);
	const { getRowOffset } = useWorklist();

	// Oldest first.
	const sortedAlerts = useMemo(() => sortAppointmentCancelledAlerts(alerts), [alerts]);

	const [alertModal, setAlertModal] = useState<{ entity: Task | null; isVisible: boolean }>({
		entity: null,
		isVisible: false,
	});

	const edit = (alert: Task) => setAlertModal({ entity: alert, isVisible: true });
	const cancelAlertModal = () => setAlertModal((prev) => ({ ...prev, isVisible: false }));
	// The modal's own save invalidates the shared task query keys; invalidate()
	// additionally refreshes this section's count.
	const completeAlertModal = () => {
		setAlertModal((prev) => ({ ...prev, isVisible: false }));
		invalidate();
	};

	const rowOffset = getRowOffset(APPOINTMENT_CANCELLED_SECTION_ID);

	return (
		<>
			<WorklistSection
				sectionId={APPOINTMENT_CANCELLED_SECTION_ID}
				titleKey="HOME.WORKLIST.APPOINTMENT_CANCELLED.TITLE"
				loadingMessageKey="HOME.WORKLIST.APPOINTMENT_CANCELLED.LOADING"
				errorMessageKey="HOME.WORKLIST.APPOINTMENT_CANCELLED.ERROR"
				count={count}
				isLoading={isLoading}
				isError={isError}
				isEmpty={sortedAlerts.length === 0}
				defaultExpanded={true}
			>
				<ul className="worklist-rows">
					{sortedAlerts.map((alert, position) => (
						<CriticalAlertRow
							key={alert.id}
							alert={alert}
							variant="appointment-cancelled"
							index={rowOffset + position + 1}
							onResolve={resolve}
							onEdit={edit}
							isResolving={isPending}
						/>
					))}
				</ul>
			</WorklistSection>
			{alertModal.isVisible && alertModal.entity && (
				<UiModalAlert
					entity={alertModal.entity}
					isVisible={alertModal.isVisible}
					onCancel={cancelAlertModal}
					onComplete={completeAlertModal}
				/>
			)}
		</>
	);
};

export default AppointmentCancelledSection;
